<template>
  <Component :is="as" class="card" :class="[notchSize, notchPosition]">
    <slot />
  </Component>
</template>

<script setup lang="ts">
defineProps({
  as: {
    type: String,
    default: 'div',
  },
  notchSize: {
    type: String as PropType<'big' | 'medium' | 'small' | 'none'>,
    default: 'none',
    validator: (value: string) => ['big', 'medium', 'small', 'none'].includes(value),
  },
  notchPosition: {
    type: String as PropType<'top' | 'bottom'>,
    default: 'top',
    validator: (value: string) => ['top', 'bottom'].includes(value),
  },
});
</script>

<style lang="css" scoped>
/* rtl:begin:ignore */

.card.big {
  --pillar-size: 34px;
  --circle-r: 13px;
  --circle-d: calc(var(--circle-r) * 2);
  --big-circle-r: 30px;
  --circles-space: 65px;
  --circle-extend-inset: calc((var(--circle-d) / 2) + var(--circles-space));
  border-radius: 20px;
}
.card.big::before {
  border-radius: 20px;
}

.card.medium {
  --pillar-size: 24px;
  --circle-r: 10px;
  --circle-d: calc(var(--circle-r) * 2);
  --big-circle-r: 25px;
  --circles-space: 50px;
  --circle-extend-inset: calc((var(--circle-d) / 2) + var(--circles-space));

  border-radius: 20px;
}
.card.medium::before {
  border-radius: 20px;
}

.card.small {
  --pillar-size: 23px;
  --circle-r: 20px;
  --circle-d: calc(var(--circle-r) * 2);
  --big-circle-r: 22px;
  --circles-space: 46px;
  --circle-extend-inset: calc((var(--circle-d) / 2) + var(--circles-space));

  border-radius: 20px;
}
.card.small::before {
  border-radius: 20px;
}

.card::before {
  content: '';
  display: block;
  border-top-right-radius: 0;
  filter: drop-shadow(var(--card-shadow));
  position: absolute;
  inset: 0;
  z-index: -1; /* Background fallback */
  background: var(--card-color);
  background: /* Top Circle */
    radial-gradient(circle, var(--card-color) 70%, transparent calc(70% + 1px)) calc(100% - var(--circles-space)) 0 /
      var(--circle-d) var(--circle-d) no-repeat no-repeat,
    /* Top Circle Extend */ linear-gradient(0deg, var(--card-color) 100%, var(--card-color) 100%) 0px 0px /
      calc(100% - var(--circle-extend-inset)) var(--circle-d) no-repeat no-repeat,
    /*
Right Gap fill Pillar */ linear-gradient(0deg, var(--card-color) 100%, var(--card-color) 100%) 0px
      var(--circle-r) / calc(100% - var(--circles-space)) calc(100% - var(--circle-r)) no-repeat no-repeat,
    /* Right Circle */ radial-gradient(circle, var(--card-color) 70%, transparent calc(70% + 1px)) 100%
      var(--circles-space) / var(--circle-d) var(--circle-d) no-repeat no-repeat,
    /* Right Circle Extend */ linear-gradient(0deg, var(--card-color) 100%, var(--card-color) 100%) 100%
      var(--circle-extend-inset) / var(--circle-d) 100% no-repeat no-repeat,
    /* Right Gap fill
Pillar */ linear-gradient(0deg, var(--card-color) 100%, var(--card-color) 100%) 0
      var(--circles-space) / calc(100% - var(--circle-r)) 100% no-repeat no-repeat,
    /* Big Background */
      radial-gradient(
        circle at 100% 0%,
        transparent var(--big-circle-r),
        var(--card-color) calc(var(--big-circle-r) + 1px)
      )
      0px var(--pillar-size) / calc(100% - var(--pillar-size)) 100% no-repeat no-repeat;
}

.card {
  --card-color: #fff;
  --card-shadow: 5px 8px 10px rgba(21, 44, 115, 0.15);

  position: relative;
  border-top-right-radius: 0;
  z-index: 0;
}

.card.none {
  @apply shadow-custom-1;
  background: var(--card-color);
  border-radius: 20px;
}
.card.bottom::before {
  transform: rotate(180deg) scaleX(-1);
}

[dir='rtl'] .card::before {
  transform: scaleX(-1);
}
[dir='rtl'] .card.bottom::before {
  transform: rotate(180deg);
}
/* rtl:end:ignore */
</style>
